import { Injectable } from '@angular/core';
import { NbAlertModalComponent } from '@app/common/basic/components/nb-alert-modal/nb-alert-modal.component';
import { ModalService } from '@app/common/basic/services/modal.service';
import { defaults } from 'lodash';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs';

export type AlertMode = 'confirm' | 'info' | 'warning' | 'error' | 'success';

interface IAlertOptions {
  message: string;
  icon?: string;
  title?: string;
  mode?: AlertMode;

  // optional callback function for top right cross sign
  onDismiss?: (modalResult: Subject<any>, modalRef: BsModalRef) => void;
  // optional callback function for close button
  onClose?: (modalResult: Subject<any>, modalRef: BsModalRef) => void;
}

@Injectable()
export class AlertService {
  constructor(private modalService: ModalService) {}

  private alert(opt: IAlertOptions): Observable<boolean> {
    const { message, icon, mode, title, onDismiss, onClose } = opt;
    return this.modalService.open<boolean>({
      template: NbAlertModalComponent,
      backdrop: 'static',
      draggable: true,
      resizable: false,
      class: 'mt-3',
      initialState: {
        description: message,
        icon,
        mode,
        title,
        onDismiss,
        onClose,
      },
    });
  }

  private generateOptions(opt: string | IAlertOptions, defaultOptions: Partial<IAlertOptions>) {
    return defaults<IAlertOptions, Partial<IAlertOptions>>(
      typeof opt === 'string' ? { message: opt } : opt,
      defaultOptions,
    );
  }

  confirm(opt: string | IAlertOptions) {
    const defaultOptions: Partial<IAlertOptions> = {
      title: 'Confirmation',
      icon: '<i class="fa fa-question"></i>',
      mode: 'confirm',
    };

    const options: IAlertOptions = this.generateOptions(opt, defaultOptions);

    return this.alert(options);
  }

  // tslint:disable-next-line: no-identical-functions
  error(opt: string | IAlertOptions) {
    const defaultOptions: Partial<IAlertOptions> = {
      title: 'Error',
      icon: '<i class="fa fa-exclamation-triangle"></i>',
      mode: 'error',
    };
    const options: IAlertOptions = this.generateOptions(opt, defaultOptions);
    return this.alert(options);
  }

  // tslint:disable-next-line: no-identical-functions
  warning(opt: string | IAlertOptions) {
    const defaultOptions: Partial<IAlertOptions> = {
      title: 'Warning',
      icon: '<i class="fa fa-exclamation-circle"></i>',
      mode: 'warning',
    };

    const options: IAlertOptions = this.generateOptions(opt, defaultOptions);
    return this.alert(options);
  }

  // tslint:disable-next-line: no-identical-functions
  info(opt: string | IAlertOptions) {
    const defaultOptions: Partial<IAlertOptions> = {
      title: 'Information',
      icon: '<i class="fa fa-info-circle"></i>',
      mode: 'info',
    };
    const options: IAlertOptions = this.generateOptions(opt, defaultOptions);

    return this.alert(options);
  }

  // tslint:disable-next-line: no-identical-functions
  success(opt: string | IAlertOptions) {
    const defaultOptions: Partial<IAlertOptions> = {
      title: 'Success',
      icon: '<i class="fa fa-check"></i>',
      mode: 'success',
    };

    const options: IAlertOptions = this.generateOptions(opt, defaultOptions);
    return this.alert(options);
  }
}
